import { useEffect, useState } from 'react';
import EChart from '../../components/Charts/EChart';
import { errorsApi } from '../../services/api';
import toast from 'react-hot-toast';

export default function ErrorsTab({ dateRange }) {
  const [loading, setLoading] = useState(true);
  const [errorStats, setErrorStats] = useState(null);
  const [recentErrors, setRecentErrors] = useState([]);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const getDaysFromRange = () => {
    return dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
  };

  async function loadData() {
    try {
      setLoading(true);
      const days = getDaysFromRange();

      // Load error statistics (if you have an endpoint for this)
      // For now, we'll load recent errors and derive stats from them
      const params = {
        limit: 100,
        days: days
      };

      const errors = await errorsApi.list(params);
      setRecentErrors(errors.errors || []);

      // Calculate statistics from errors
      const stats = calculateErrorStats(errors.errors || []);
      setErrorStats(stats);
    } catch (error) {
      console.error('Failed to load error analytics:', error);
      toast.error('Failed to load error analytics');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  const sourceChartData = transformSourceData(errorStats);
  const severityChartData = transformSeverityData(errorStats);
  const timelineChartData = transformTimelineData(errorStats);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <MetricCard
          title="Total Errors"
          value={errorStats?.totalErrors || 0}
          subtitle={`In last ${dateRange}`}
          icon="⚠️"
          color="red"
        />
        <MetricCard
          title="Critical Errors"
          value={errorStats?.criticalCount || 0}
          subtitle="Requires immediate attention"
          icon="🔴"
          color="red"
        />
        <MetricCard
          title="Affected Users"
          value={errorStats?.uniqueUsers || 0}
          subtitle="Users who experienced errors"
          icon="👥"
          color="orange"
        />
        <MetricCard
          title="Error Rate"
          value={`${(errorStats?.errorRate || 0).toFixed(2)}%`}
          subtitle="Percentage of operations failing"
          icon="📊"
          color="yellow"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Errors by Source */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Errors by Source</h2>
          <EChart option={sourceChartData} height="300px" />
        </div>

        {/* Errors by Severity */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Errors by Severity</h2>
          <EChart option={severityChartData} height="300px" />
        </div>
      </div>

      {/* Error Timeline */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Error Trend</h2>
        <EChart option={timelineChartData} height="250px" />
      </div>

      {/* Recent Errors */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Errors</h2>
        {recentErrors.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No errors recorded in this time period</p>
            <p className="text-sm text-green-600 mt-2">✓ System is running smoothly!</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {recentErrors.slice(0, 20).map((error) => (
              <ErrorCard key={error.id} error={error} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function calculateErrorStats(errors) {
  const stats = {
    totalErrors: errors.length,
    criticalCount: 0,
    errorCount: 0,
    warningCount: 0,
    infoCount: 0,
    uniqueUsers: new Set(),
    bySource: {},
    byDay: {},
    errorRate: 0
  };

  errors.forEach(error => {
    // Count by severity
    switch (error.severity) {
      case 'critical':
        stats.criticalCount++;
        break;
      case 'error':
        stats.errorCount++;
        break;
      case 'warning':
        stats.warningCount++;
        break;
      case 'info':
        stats.infoCount++;
        break;
    }

    // Count by source
    const source = error.source || 'unknown';
    stats.bySource[source] = (stats.bySource[source] || 0) + 1;

    // Track unique users
    if (error.user_id) {
      stats.uniqueUsers.add(error.user_id);
    }

    // Count by day
    const date = new Date(error.created_at).toISOString().split('T')[0];
    stats.byDay[date] = (stats.byDay[date] || 0) + 1;
  });

  stats.uniqueUsers = stats.uniqueUsers.size;
  stats.errorRate = errors.length > 0 ? (errors.length / 1000) * 100 : 0; // Simplified calculation

  return stats;
}

function transformSourceData(stats) {
  if (!stats || !stats.bySource || Object.keys(stats.bySource).length === 0) {
    return {
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value', name: 'Errors' },
      series: []
    };
  }

  const sources = Object.keys(stats.bySource);
  const data = Object.values(stats.bySource);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    xAxis: {
      type: 'category',
      data: sources.map(formatSource),
      axisLabel: {
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: 'Number of Errors',
      splitLine: {
        lineStyle: { type: 'dashed' }
      }
    },
    series: [
      {
        name: 'Errors',
        type: 'bar',
        data: data,
        itemStyle: {
          color: '#ef4444',
          borderRadius: [4, 4, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          formatter: (params) => params.value.toLocaleString()
        }
      }
    ]
  };
}

function transformSeverityData(stats) {
  if (!stats) {
    return {
      series: []
    };
  }

  const chartData = [
    { value: stats.criticalCount || 0, name: 'Critical' },
    { value: stats.errorCount || 0, name: 'Error' },
    { value: stats.warningCount || 0, name: 'Warning' },
    { value: stats.infoCount || 0, name: 'Info' }
  ];

  return {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'horizontal',
      bottom: 0,
      data: ['Critical', 'Error', 'Warning', 'Info']
    },
    series: [
      {
        name: 'Errors',
        type: 'pie',
        radius: ['50%', '70%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          formatter: '{c}'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: true
        },
        data: chartData,
        color: ['#dc2626', '#ef4444', '#f59e0b', '#3b82f6']
      }
    ]
  };
}

function transformTimelineData(stats) {
  if (!stats || !stats.byDay || Object.keys(stats.byDay).length === 0) {
    return {
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value', name: 'Errors' },
      series: []
    };
  }

  const sortedDates = Object.keys(stats.byDay).sort();
  const categories = sortedDates.map(date => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });
  const data = sortedDates.map(date => stats.byDay[date]);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' }
    },
    xAxis: {
      type: 'category',
      data: categories,
      boundaryGap: false,
      axisLabel: {
        rotate: 45,
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: 'Number of Errors',
      splitLine: {
        lineStyle: { type: 'dashed' }
      }
    },
    series: [
      {
        name: 'Errors',
        type: 'line',
        data: data,
        smooth: true,
        itemStyle: { color: '#ef4444' },
        lineStyle: { width: 3 },
        symbol: 'circle',
        symbolSize: 6,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(239, 68, 68, 0.3)' },
              { offset: 1, color: 'rgba(239, 68, 68, 0.05)' }
            ]
          }
        }
      }
    ]
  };
}

function ErrorCard({ error }) {
  const severityColors = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    error: 'bg-orange-100 text-orange-800 border-orange-300',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    info: 'bg-blue-100 text-blue-800 border-blue-300'
  };

  const severityIcons = {
    critical: '🔴',
    error: '🟠',
    warning: '🟡',
    info: 'ℹ️'
  };

  return (
    <div className={`rounded-lg border-l-4 p-4 ${severityColors[error.severity]}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{severityIcons[error.severity]}</span>
            <span className="font-semibold uppercase text-xs">
              {error.severity}
            </span>
            <span className="text-xs opacity-75">
              {formatSource(error.source)}
            </span>
          </div>
          <p className="text-sm font-medium mb-1">{error.error_message}</p>
          <div className="flex items-center gap-3 text-xs opacity-75">
            <span>{new Date(error.created_at).toLocaleString()}</span>
            {error.user_id && <span>User: {error.user_id.slice(0, 8)}...</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, subtitle, icon, color }) {
  const colorClasses = {
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-orange-600',
    yellow: 'bg-yellow-50 text-yellow-600'
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow card-hover">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <span className={`text-2xl p-2 rounded-lg ${colorClasses[color]}`}>{icon}</span>
      </div>
      <p className="text-3xl font-bold text-gray-900">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      <p className="text-sm text-gray-500 mt-2">{subtitle}</p>
    </div>
  );
}

function formatSource(source) {
  if (!source) return 'Unknown';
  return source.charAt(0).toUpperCase() + source.slice(1);
}
